import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './client.ts';
import { useSession } from '../auth/session.tsx';
import { queryDefaults } from './queries.ts';

/**
 * A client's bonds, recorded against their orders (162, P-35; api/src/routes/bonds.ts). The bond is the one source: its
 * issued date completes the order's "Procure the bond" deadline, whichever screen wrote it. Amounts arrive as the
 * database's text and are shown as they are, never through a float.
 */

export interface Bond {
  id: string; court_order_id: string | null; order_signed_date: string | null; order_type: string | null;
  amount: string | null; applied_on: string | null; granted_on: string | null; granted_date_unknown: boolean;
  increases_bond_id: string | null; surety_name: string | null; bond_number: string | null; has_document: boolean;
  upload_id: string | null; clio_copy_id: string | null;
  withdrawn: boolean; withdrawal_reason: string | null;
  /** The bond's fields as a version holds them: a correction sends them whole. */
  fields: Record<string, unknown>;
  versions: number;
}
export interface BondOrder {
  id: string; signed_date: string | null; order_type: string | null; bond_required: boolean | null;
  deadline_id: string | null; deadline_status: string | null; due_date: string | null; completed_on: string | null;
  completion_date_unknown: boolean | null; met_by_bond: boolean | null;
}
export interface BondEvidence {
  id: string; kind: string; whose: 'agency' | 'predecessor guardian' | 'undecided'; date_in_filename: string | null;
  amount_in_filename: string | null; client_bond_id: string | null;
}
export interface ClientBonds {
  bonds: Bond[]; orders: BondOrder[]; evidence: BondEvidence[]; may_record: boolean; may_edit: boolean;
}

export function useClientBonds(clientId: string, enabled = true) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['client', clientId, 'bonds'],
    queryFn: () => apiFetch<ClientBonds>(`/clients/${clientId}/bonds`, getToken),
    ...queryDefaults,
    enabled,
  });
}

function post<T>(getToken: () => Promise<string>, path: string, body: unknown): Promise<T> {
  return apiFetch<T>(path, getToken, {
    method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body),
  });
}

/** Everything a bond changes: the client's bonds, its order and deadline, the tasks the deadline holds, the tracker. */
function useBondChanged(clientId: string) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['client', clientId] });
    void queryClient.invalidateQueries({ queryKey: ['tasks'] });
    void queryClient.invalidateQueries({ queryKey: ['commissions'] });
  };
}

export interface BondInput {
  amount?: string | null; applied_on?: string | null; granted_on?: string | null; granted_date_unknown?: boolean;
  increases_bond_id?: string | null; surety_name?: string | null; bond_number?: string | null;
}

export function useRecordBond(clientId: string) {
  const { getToken } = useSession();
  const changed = useBondChanged(clientId);
  return useMutation({
    mutationFn: ({ orderId, ...body }: BondInput & { orderId: string }) =>
      post<{ id: string }>(getToken, `/court_orders/${orderId}/bonds`, body),
    onSuccess: changed,
  });
}

export function useCorrectBond(clientId: string) {
  const { getToken } = useSession();
  const changed = useBondChanged(clientId);
  return useMutation({
    mutationFn: ({ bondId, ...body }: { bondId: string; fields: Record<string, unknown>; reason: string; reason_text?: string }) =>
      post<{ version: number }>(getToken, `/bonds/${bondId}/correct`, body),
    onSuccess: changed,
  });
}

export function useWithdrawBond(clientId: string) {
  const { getToken } = useSession();
  const changed = useBondChanged(clientId);
  return useMutation({
    mutationFn: ({ bondId, reason }: { bondId: string; reason: string }) =>
      post<{ version: number }>(getToken, `/bonds/${bondId}/withdraw`, { reason }),
    onSuccess: changed,
  });
}

/** How a bond's issue reads: "Issued 3 Jan 2025", "Issued, date not known", "Applied for, not yet issued", "No date recorded". */
export function bondState(b: Pick<Bond, 'granted_on' | 'granted_date_unknown' | 'applied_on'>, fmt: (d: string) => string): string {
  if (b.granted_on) return `Issued ${fmt(b.granted_on)}`;
  if (b.granted_date_unknown) return 'Issued, date not known';
  if (b.applied_on) return `Applied for ${fmt(b.applied_on)}, not yet issued`;
  return 'No date recorded: not yet applied for or issued';
}

/** The order's "Procure the bond" deadline, in words. */
export function procuredSaid(o: Pick<BondOrder, 'deadline_status' | 'completed_on' | 'completion_date_unknown' | 'met_by_bond' | 'due_date'>,
                             fmt: (d: string) => string): string {
  if (!o.deadline_status) return 'No "Procure the bond" deadline on this order.';
  if (o.deadline_status === 'Complete') {
    const when = o.completed_on ? `on ${fmt(o.completed_on)}` : 'on a date not known';
    return o.met_by_bond ? `Procured ${when} (by the bond recorded here).` : `Procured ${when} (recorded before bonds were recorded here).`;
  }
  if (o.deadline_status === 'Upcoming' || o.deadline_status === 'In Progress') {
    return `Not yet procured${o.due_date ? `: due ${fmt(o.due_date)}` : ''}.`;
  }
  return `${o.deadline_status}.`;
}
